<template>
    <div class="modal fade" id="bulkCostModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('views.inventory.modals.bulkCost.title') }}</h5>
                    <button type="button" class="btn-close" @click="$emit('update:modelValue', false)"></button>
                </div>
                <div class="modal-body">
                    <div class="row mb-1">
                        <div class="col-sm-7">
                            <FileUpload :key="fileUploadKey" v-model="file" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" @change="handleFileChange" />
                        </div>
                        <div class="col-sm-5">
                            <button class="btn btn-secondary h-100 w-100" :disabled="!data" @click="handleShowTemplateModal">
                                {{ $t('actions.edit_template') }}
                            </button>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col-sm-12">
                            <a href="#" class="d-flex align-items-center text-primary" style="margin-top: 10px" @click.prevent="downloadTemplate">
                                <span>{{ $t('views.inventory.modals.bulkCost.downloadTemplate') }}</span>
                                <i v-if="isDownloading" class="bi-arrow-clockwise spin"></i>
                                <i v-else class="bi-download"></i>
                            </a>
                        </div>
                    </div>
                    <div class="row align-items-center">
                        <div class="col-sm-12">
                            <label class="form-label">{{ $t('views.inventory.modals.bulkCost.currency') }}</label>
                            <select v-model="currency" class="form-select">
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="CAD">CAD</option>
                                <option value="TRY">TRY</option>
                            </select>
                        </div>
                    </div>
                    <div class="row mt-4">
                        <div class="col-sm-12 d-flex align-items-end">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" id="overrideCheck" v-model="override" />
                                <label class="form-check-label" for="overrideCheck">
                                    {{ $t('views.inventory.modals.bulkCost.override') }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-white" @click="$emit('update:modelValue', false)">
                        {{ $t('actions.cancel') }}
                    </button>
                    <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="!isValid">
                        {{ $t('actions.complete') }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <TemplateOptions
        :data="data"
        v-model:data="data"
        :showModal="showTemplateModal"
        @closeModal="handleCloseTemplateModal"
        @saveTemplateOptions="handleTemplateOptions"
        :mappingConfig="{
            fields: {
                sku: { label: $t('views.inventory.modals.bulkCost.fields.sku'), required: true },
                price: { label: $t('views.inventory.modals.bulkCost.fields.price'), required: false, type: 'number' },
            },
            validationRules: {
                price: {
                    pattern: /^\d+(\.\d+)?$/,
                    message: 'components.templateOptions.validation.priceFormat',
                    fix: function (value) {
                        if (!value) return '0.00'

                        // Remove currency symbols and spaces
                        value = value.replace(/[₺$€£]/g, '').trim()

                        // Handle European number format (1.348,80 -> 1348.80)
                        if (/^\d{1,3}(?:\.\d{3})*(?:,\d+)?$/.test(value)) {
                            value = value.replace(/\./g, '').replace(',', '.')
                        }

                        // Handle comma as decimal separator (12,10 -> 12.10)
                        if (/^\d+,\d+$/.test(value)) {
                            value = value.replace(',', '.')
                        }

                        // Handle basic mathematical expressions
                        if (/^[\d\s+\-*/().]+$/.test(value)) {
                            try {
                                const cleanExpr = value.replace(/\s+/g, '')
                                const result = eval(cleanExpr)
                                return result.toString()
                            } catch (e) {
                                value = value.replace(/[^\d.]/g, '')
                            }
                        }

                        value = value.replace(/[^\d.]/g, '')
                        const parts = value.split('.')
                        if (parts.length > 2) value = parts[0] + '.' + parts.slice(1).join('')

                        const num = parseFloat(value)
                        return isNaN(num) ? '0.00' : num.toString()
                    },
                },
            },
            columnPatterns: {
                sku: ['sku', 'asin', 'product id', 'product_id', 'productid', 'urun kodu', 'ürün kodu'],
                price: ['cost', 'price', 'maliyet', 'ürün maliyeti', 'product cost', 'fiyat'],
            },
        }"
    />
</template>

<script>
import { modalManager } from '@/modal-manager.service'
import * as XLSX from 'xlsx'
import FileUpload from '@/components/FileUpload.vue'
import TemplateOptions from '@/components/TemplateOptions.vue'
import InventoryService from '@/services/inventory'

export default {
    name: 'BulkCostModal',
    components: {
        FileUpload,
        TemplateOptions,
    },
    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['update:modelValue', 'save'],
    data() {
        return {
            file: null,
            data: null,
            currency: 'USD',
            override: false,
            showTemplateModal: false,
            templateOptions: {
                headingRow: 1,
                defaults: {
                    sku: '',
                    price: '',
                },
            },
            isDownloading: false,
            fileUploadKey: 0,
        }
    },
    computed: {
        isValid() {
            return this.file && this.data && this.templateOptions.defaults.sku && this.templateOptions.defaults.price
        },
    },
    methods: {
        resetForm() {
            this.file = null
            this.data = null
            this.currency = 'USD'
            this.override = false
            this.showTemplateModal = false
            this.templateOptions = {
                headingRow: 1,
                defaults: {
                    sku: '',
                    price: '',
                },
            }
            this.isDownloading = false
            this.fileUploadKey++
        },
        handleFileChange() {
            if (this.file && this.file[0]) {
                const reader = new FileReader()
                if (!this.file[0].name.endsWith('.xlsx')) {
                    this.$toast.error(this.$t('common.invalid_file_type'))
                    return
                }
                reader.onload = (e) => {
                    const bstr = e.target.result
                    const wb = XLSX.read(bstr, { type: 'binary' })
                    const wsname = wb.SheetNames[0]
                    const ws = wb.Sheets[wsname]
                    this.data = XLSX.utils.sheet_to_json(ws, { header: 1 })
                }
                reader.readAsBinaryString(this.file[0])
            }
        },
        async handleShowTemplateModal() {
            try {
                modalManager.hideModal('bulkCostModal')
                await this.$nextTick()
                modalManager.showModal('templateOptionsModal', {
                    backdrop: 'static',
                    keyboard: false,
                })
                this.showTemplateModal = true
            } catch (error) {
                console.error('Error showing template modal:', error)
                this.$toast.error(this.$t('common.error'))
            }
        },
        async handleCloseTemplateModal() {
            try {
                modalManager.hideModal('templateOptionsModal')
                await this.$nextTick()
                modalManager.showModal('bulkCostModal')
                this.showTemplateModal = false
            } catch (error) {
                console.error('Error closing template modal:', error)
                this.$toast.error(this.$t('common.error'))
            }
        },
        s2ab(s) {
            const buf = new ArrayBuffer(s.length)
            const view = new Uint8Array(buf)
            for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff
            return buf
        },
        handleTemplateOptions(data) {
            if (data) {
                this.templateOptions = {
                    headingRow: data.headingRow,
                    defaults: data.defaults,
                }
                console.log(data.updatedData)
                if (data.updatedData) {
                    const wb = XLSX.utils.book_new()
                    const ws = XLSX.utils.json_to_sheet(data.updatedData, { skipHeader: true })
                    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')

                    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
                    const blob = new Blob([this.s2ab(wbout)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

                    const fileName = this.file[0].name.replace('.xlsx', '_corrected.xlsx')
                    this.file = [new File([blob], fileName, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })]
                }
            }
            this.handleCloseTemplateModal()
        },
        handleSubmit() {
            // Validation checks
            if (!this.file || !this.file[0]) {
                this.$toast.error(this.$t('views.inventory.modals.bulkCost.validation.fileRequired'))
                return
            }

            if (!this.templateOptions.headingRow) {
                this.$toast.error(this.$t('views.inventory.modals.bulkCost.validation.headingRowRequired'))
                return
            }

            if (!this.templateOptions.defaults.sku) {
                this.$toast.error(this.$t('views.inventory.modals.bulkCost.validation.skuColumnRequired'))
                return
            }

            if (!this.templateOptions.defaults.price) {
                this.$toast.error(this.$t('views.inventory.modals.bulkCost.validation.priceColumnRequired'))
                return
            }

            const formData = new FormData()
            formData.append('file', this.file[0])
            formData.append('headingRow', this.templateOptions.headingRow)
            formData.append('currency', this.currency)
            formData.append('override', this.override)
            formData.append('rowCount', this.templateOptions.rowCount)

            Object.entries(this.templateOptions.defaults).forEach(([key, value]) => {
                formData.append(`options[rows][${key}]`, value)
            })

            this.$emit('save', formData)
        },
        async downloadTemplate() {
            try {
                this.isDownloading = true
                const response = await InventoryService.getBulkCostTemplate()

                // Create workbook and worksheet
                const wb = XLSX.utils.book_new()
                const ws = XLSX.utils.json_to_sheet(response.data, {
                    header: ['sku', 'price'],
                })

                // Add worksheet to workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Template')

                // Generate and download file
                XLSX.writeFile(wb, 'bulk_cost_template.xlsx')
            } catch (error) {
                console.error('Template download error:', error)
                this.$toast.error(this.$t('views.inventory.modals.bulkCost.downloadTemplateError'))
            } finally {
                this.isDownloading = false
            }
        },
    },
    watch: {
        async modelValue(newValue) {
            try {
                if (newValue) {
                    await this.$nextTick()
                    modalManager.showModal('bulkCostModal')
                } else {
                    modalManager.hideModal('bulkCostModal')
                    this.resetForm()
                }
            } catch (error) {
                console.error('Error handling modal value change:', error)
                this.$toast.error(this.$t('common.error'))
            }
        },
    },
    beforeUnmount() {
        modalManager.destroyModal('bulkCostModal')
        modalManager.destroyModal('templateOptionsModal')
    },
    async mounted() {
        try {
            modalManager.initModal('bulkCostModal')
            modalManager.initModal('templateOptionsModal')
        } catch (error) {
            console.error('Error initializing modals:', error)
            this.$toast.error(this.$t('common.error'))
        }
    },
}
</script>

<style scoped>
.modal-dialog {
    max-width: 500px;
}

.btn-secondary {
    min-height: 38px;
}

.form-check {
    margin-bottom: 0;
}

.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
